import * as THREE from 'three';
import { SNAPSHOT_3D, SNAPSHOT_2D, SNAPSHOT_CONFIG } from './snapshotConfig';

/**
 * Snapshot Capture Utility
 * Captures 2D and 3D views of the Three.js scene without disrupting the live view.
 *
 * Key fixes vs original implementation:
 *  1. Uses camera.lookAt(0,0,0) instead of camera.rotation.set() — Euler rotation
 *     values from the Three.js inspector are OUTPUT values, not inputs.
 *  2. Temporarily rotates the scene for 3D snapshots if the scene is currently
 *     in 2D/unrotated state (mirrors what App.rotateScene() does).
 *  3. Fully restores scene rotation and renderer size after every capture.
 */
class SnapshotCapture {
  /**
   * @param {THREE.WebGLRenderer} renderer
   * @param {THREE.Scene} scene
   * @param {CameraManager} cameraManager
   * @param {App} app  — needed to read/set sceneRotated state
   */
  constructor(renderer, scene, cameraManager, app) {
    this.renderer = renderer;
    this.scene = scene;
    this.cameraManager = cameraManager;
    this.app = app; // reference to App instance for scene rotation state
  }

  // ─────────────────────────────────────────────────────────────
  // PRIVATE HELPERS
  // ─────────────────────────────────────────────────────────────

  /**
   * Store original material properties and make all materials opaque for snapshot.
   * Traverses the entire scene and temporarily sets opacity to 1.0 and transparent to false.
   * @returns {Array} Array of material state objects to restore later
   */
  _makeAllMaterialsOpaque() {
    const materialStates = [];

    this.scene.traverse((object) => {
      if (object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        
        materials.forEach((material) => {
          if (material && (material.transparent || material.opacity < 1.0)) {
            // Store original state
            materialStates.push({
              material: material,
              originalOpacity: material.opacity,
              originalTransparent: material.transparent
            });
            
            // Make opaque for snapshot
            material.opacity = 1.0;
            material.transparent = false;
            material.needsUpdate = true;
          }
        });
      }
    });

    console.log(`📸 [SNAPSHOT] Made ${materialStates.length} materials opaque for capture`);
    return materialStates;
  }

  /**
   * Restore original material properties after snapshot capture.
   * @param {Array} materialStates Array of material state objects from _makeAllMaterialsOpaque
   */
  _restoreMaterialStates(materialStates) {
    materialStates.forEach(({ material, originalOpacity, originalTransparent }) => {
      material.opacity = originalOpacity;
      material.transparent = originalTransparent;
      material.needsUpdate = true;
    });
    console.log(`📸 [SNAPSHOT] Restored ${materialStates.length} materials to original state`);
  }

  /**
   * Rotate all scene children -90° on X axis (same as App.rotateScene).
   * Used to put the scene into 3D orientation for the 3D snapshot.
   */
  _rotateSceneTo3D() {
    for (const child of this.scene.children) {
      child.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
    }
  }

  /**
   * Rotate all scene children back +90° on X axis (same as App.rotateSceneBack).
   */
  _rotateSceneBack() {
    for (const child of this.scene.children) {
      child.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), Math.PI / 2);
    }
  }

  /**
   * Core low-level capture.
   * Assumes scene is already in the correct rotation state for the requested mode.
   *
   * @param {'2d'|'3d'} mode
   * @param {number} scale  supersampling multiplier
   * @returns {string} PNG data URL
   */
  _renderAndCapture(mode, scale) {
    // ── Save complete renderer state ──────────────────────────────
    const canvas = this.renderer.domElement;
    const originalSize = this.renderer.getSize(new THREE.Vector2());
    const originalPixelRatio = this.renderer.getPixelRatio();
    const originalViewport = new THREE.Vector4();
    this.renderer.getViewport(originalViewport);

    try {
      // ── 1. Upscale renderer for high-res output ────────────────
      if (scale > 1) {
        this.renderer.setSize(
          originalSize.width * scale, 
          originalSize.height * scale, 
          false  // Don't update CSS style
        );
      }

      const aspect = originalSize.width / originalSize.height;

      // ── 2. Build a temporary camera — never mutate the active one ─
      let tempCamera;

      if (mode === '3d') {
        const cfg = SNAPSHOT_3D;
        tempCamera = new THREE.PerspectiveCamera(
          cfg.fov,
          aspect,
          this.cameraManager.perspectiveCamera?.near ?? 0.1,
          this.cameraManager.perspectiveCamera?.far  ?? 2000
        );

        const d = 60; // pull-back distance — increase if building is large

        // ── Option A: straight front at 45° ──────────────────────────────────────
        tempCamera.position.set(0, d, d);

        // ── Option B: diagonal isometric (uncomment to use) ──────────────────────
        // tempCamera.position.set(d, d, d);

        // ── Option C: right-side inclined (uncomment to use) ─────────────────────
        // tempCamera.position.set(d, d, 0);

        tempCamera.up.set(0, 1, 0);   // keep Y as "up" so image isn't sideways
        tempCamera.lookAt(cfg.lookAt); // cfg.lookAt = new THREE.Vector3(0, 0, 0)
      } else {
        const cfg = SNAPSHOT_2D;
        const h = cfg.frustumSize;
        tempCamera = new THREE.OrthographicCamera(
          -h * aspect,  // left
           h * aspect,  // right
           h,           // top
          -h,           // bottom
          0.1,
          2000
        );
        tempCamera.position.copy(cfg.position);

        // ✅ THE FIX: same here — lookAt, not rotation.set()
        tempCamera.lookAt(cfg.lookAt);
      }

      tempCamera.updateMatrixWorld();

      // ── 3. Render one frame with the temporary camera ───────────
      this.renderer.render(this.scene, tempCamera);

      // ── 4. Read pixels immediately (preserveDrawingBuffer must be true)
      const dataURL = canvas.toDataURL(SNAPSHOT_CONFIG.format);

      return dataURL;

    } finally {
      // ── 5. ALWAYS restore renderer state (even if error occurred) ──
      this.renderer.setSize(
        originalSize.width, 
        originalSize.height, 
        false  // Don't update CSS style
      );
      this.renderer.setPixelRatio(originalPixelRatio);
      // ✅ setViewport expects 4 numbers, not a Vector4 object
      this.renderer.setViewport(
        originalViewport.x,
        originalViewport.y,
        originalViewport.z,  // width stored in z
        originalViewport.w   // height stored in w
      );
    }
  }

  // ─────────────────────────────────────────────────────────────
  // PUBLIC API
  // ─────────────────────────────────────────────────────────────

  /**
   * Capture a single snapshot.
   *
   * For the 3D mode this method will temporarily rotate the scene into 3D
   * orientation if it is currently in 2D state, then rotate it back — so the
   * live canvas is never visibly disturbed.
   *
   * @param {'2d'|'3d'} mode
   * @param {number} scale  resolution multiplier (default from config)
   * @returns {Promise<string>}  PNG data URL
   */
  async captureSnapshot(mode = '3d', scale = SNAPSHOT_CONFIG.defaultScale) {
    return new Promise((resolve, reject) => {
      console.log(`📸 [SNAPSHOT] Capturing ${mode.toUpperCase()} view at ${scale}x…`);

      // ── Scene rotation bookkeeping ──────────────────────────
      const currentlyRotated = this.app?.sceneRotated ?? false;
      let sceneWasRotated = false;
      let materialStates = [];

      try {
        // ── Step 1: Make all materials opaque for clean snapshot ──
        materialStates = this._makeAllMaterialsOpaque();

        // ── Step 2: Temporarily rotate scene if needed for the capture mode ──
        if (mode === '3d' && !currentlyRotated) {
          this._rotateSceneTo3D();
          sceneWasRotated = true;
        } else if (mode === '2d' && currentlyRotated) {
          this._rotateSceneBack();
          sceneWasRotated = true;
        }

        // ── Step 3: Render & capture ────────────────────────────────────
        const dataURL = this._renderAndCapture(mode, scale);

        console.log(`✅ [SNAPSHOT] ${mode.toUpperCase()} captured — dataURL length: ${dataURL.length}`);
        resolve(dataURL);

      } catch (err) {
        console.error(`❌ [SNAPSHOT] Failed to capture ${mode}:`, err);
        reject(err);
      } finally {
        // ── ALWAYS restore material states (even if error occurred) ──
        if (materialStates.length > 0) {
          this._restoreMaterialStates(materialStates);
        }

        // ── ALWAYS restore scene rotation (even if error occurred) ──
        if (sceneWasRotated) {
          if (mode === '3d' && !currentlyRotated) {
            this._rotateSceneBack();  // restore to 2D
          } else if (mode === '2d' && currentlyRotated) {
            this._rotateSceneTo3D();  // restore to 3D
          }
        }

        // ── ALWAYS restore live view with the real active camera ──
        try {
          this.renderer.render(this.scene, this.cameraManager.camera);
        } catch (renderErr) {
          console.error('❌ [SNAPSHOT] Failed to restore live view:', renderErr);
        }
      }
    });
  }

  /**
   * Capture both 2D and 3D views sequentially.
   *
   * @param {number} scale
   * @returns {Promise<{snapshot2D: string, snapshot3D: string}>}
   */
  async captureBothViews(scale = SNAPSHOT_CONFIG.defaultScale) {
    console.log('📸 [SNAPSHOT] Starting dual capture (2D + 3D)…');

    const snapshot2D = await this.captureSnapshot('2d', scale);

    // Small pause so the restored render-call above has settled
    await new Promise(r => setTimeout(r, SNAPSHOT_CONFIG.restoreDelayMs));

    const snapshot3D = await this.captureSnapshot('3d', scale);

    console.log('✅ [SNAPSHOT] Both views captured.');
    return { snapshot2D, snapshot3D };
  }

  /**
   * Download a captured snapshot to disk.
   *
   * @param {string} dataURL
   * @param {'2d'|'3d'} mode  used to build the filename
   */
  downloadSnapshot(dataURL, mode = '3d') {
    const filename = `${SNAPSHOT_CONFIG.filenamePrefix}-${mode}-view.png`;
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataURL;
    link.click();
    console.log(`💾 [SNAPSHOT] Downloaded: ${filename}`);
  }

  /**
   * Return a snapshot as a Blob — useful for jsPDF / pdfmake / API upload.
   *
   * @param {'2d'|'3d'} mode
   * @param {number} scale
   * @returns {Promise<Blob>}
   */
  async getSnapshotBlob(mode = '3d', scale = SNAPSHOT_CONFIG.defaultScale) {
    const dataURL = await this.captureSnapshot(mode, scale);
    const res = await fetch(dataURL);
    const blob = await res.blob();
    console.log(`✅ [SNAPSHOT] Blob ready — ${blob.size} bytes`);
    return blob;
  }
}

export default SnapshotCapture;
